import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Payload Builder (level 35) — the logic rung. Pick modules that meet the target's minimum on three
// capability axes without going over the byte budget, where a module may require another and the
// target is watching one module family.
//
// The board never changes on its own, so unlike the two rungs before it there is no ticker, no
// polling and nothing anchored to the wall clock here — the only clock is the host's countdown.
//
// The arithmetic below (totals, what is still short, family tallies) is not a duplicate of the
// grader: it is the board's own published numbers added up, which the player could do on paper. What
// is NOT here is which family the target watches — the server withholds it and a trial compile buys
// it — so this view can show what a build costs but never promise that it will be accepted.

interface Module {
  id: number;
  name: string;
  family: string;
  bytes: number;
  gain: number[];
  needs: number | null;
}

interface Challenge {
  axes: string[];
  /** False until the toolchain is opened — the library is absent from the payload until then. */
  started: boolean;
  /** How many modules the library holds. Known up front; what they are is not. */
  moduleCount: number;
  required: number[];
  budget: number;
  modules: Module[];
  /** How many of the watched family a payload may carry. */
  detectionCap: number;
  /** How many families are watched — not which. */
  detectedFamilies: number;
  /** Families a trial compile has already named. */
  knownFamilies: string[];
  dryRunsLeft: number;
  dryRunsGranted: number;
  timeLimitSec: number;
  /** Seconds of the limit set aside for reading the brief. */
  readingAllowanceSec: number;
  report?: string[] | null;
}

export default function PayloadBuilderView({
  challenge, onAct, onSubmit, setPendingSubmission, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [picked, setPicked] = useState<number[]>([]);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    setLive(challenge ?? null);
    setPicked([]);
    setNote(null);
  }, [challenge]);

  const modules = useMemo(() => live?.modules ?? [], [live]);
  const axes = useMemo(() => live?.axes ?? [], [live]);
  const required = useMemo(() => live?.required ?? [], [live]);
  const budget = live?.budget ?? 0;
  const cap = live?.detectionCap ?? 0;
  const known = useMemo(() => live?.knownFamilies ?? [], [live]);

  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };
  const chosen = useMemo(() => new Set(picked), [picked]);
  const started = live?.started ?? false;

  const begin = async () => {
    const next = await onAct({ kind: 'begin' });
    if (next) setLive(next);
  };

  // Keep the host informed, so a timeout submits what is on screen rather than nothing.
  useEffect(() => { setPendingSubmission({ modules: picked }); }, [picked, setPendingSubmission]);

  const bytes = picked.reduce((sum, id) => sum + (modules.find(m => m.id === id)?.bytes ?? 0), 0);
  const totals = axes.map((_, axis) =>
    picked.reduce((sum, id) => sum + (modules.find(m => m.id === id)?.gain[axis] ?? 0), 0));

  const missingDeps = picked
    .map(id => modules.find(m => m.id === id))
    .filter((m): m is Module => !!m && m.needs !== null && !chosen.has(m.needs))
    .map(m => ({ module: m.name, needs: modules.find(x => x.id === m.needs)?.name ?? '?' }));

  const familyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const id of picked) {
      const f = modules.find(m => m.id === id)?.family;
      if (f) counts.set(f, (counts.get(f) ?? 0) + 1);
    }
    return counts;
  }, [picked, modules]);

  const overCap = [...familyCounts.entries()].filter(([, n]) => cap > 0 && n > cap);

  const toggle = (id: number) => {
    setNote(null);
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  };

  const dryRun = async () => {
    if (picked.length === 0) { setNote('Nothing selected to compile.'); return; }
    const next = await onAct({ kind: 'dryrun', modules: picked });
    if (next) { setLive(next); setNote(null); }
  };

  const spare = budget - bytes;
  const axisShort = axes.map((_, i) => Math.max(0, (required[i] ?? 0) - totals[i]));
  const readyLooking = picked.length > 0 && spare >= 0
    && axisShort.every(s => s === 0) && missingDeps.length === 0 && overCap.length === 0;

  if (!started) {
    const cap = live?.detectionCap ?? 1;
    return (
      <div style={{ fontFamily: 'monospace' }}>
        <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117' }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            You are assembling one exploit out of a library of{' '}
            <strong>{live?.moduleCount ?? 0} modules</strong>. The target demands a minimum on three
            capability axes — <strong>{(live?.axes ?? []).join(' · ')}</strong> — and your payload has
            to clear every one of them <strong>without going over the byte budget</strong>. Pick a set
            of modules and compile it.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            Two things make that harder than adding up numbers. A module may{' '}
            <strong>require another</strong> to work, so what it really costs you is itself plus
            whatever it drags in — the most efficient part on the board is often the most expensive
            once you pay for its prerequisite. And the target runs{' '}
            <strong style={{ color: '#f87171' }}>signature detection</strong>: it will catch a payload
            carrying more than <strong>{cap}</strong> module{cap === 1 ? '' : 's'} of{' '}
            {(live?.detectedFamilies ?? 1) === 1 ? 'a certain family' : 'certain families'}.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
            <strong>Which</strong> family that is, you are not told. You never have to guess, though:
            staying at or under {cap} in <em>every</em> family is always safe, and there is always an
            answer that does. Knowing which family is watched only lets you be less careful with the
            others — useful when the budget is tight.
          </p>
          {(live?.dryRunsGranted ?? 0) > 0 ? (
            <p style={{ ...row, color: '#60a5fa', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
              Your SshExploit build affords{' '}
              <strong>{live?.dryRunsGranted} trial compile{(live?.dryRunsGranted ?? 0) === 1 ? '' : 's'}</strong>.
              A trial names the constraint that failed — and the watched family, if you trip it — but
              never what to add. One that compiles cleanly ends the job there and then.
            </p>
          ) : (
            <p style={{ ...row, color: '#fbbf24', margin: '0 0 0.6rem', lineHeight: 1.6 }}>
              Your SshExploit is below the version this target expects: no trial compiles, and a
              tighter budget than a properly equipped run would get. You compile blind.
            </p>
          )}
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.8rem' }}>
            The library is handed over when you open the toolchain. The clock is already running —
            about {live?.readingAllowanceSec ?? 30}s of it is set aside for reading this — so take the
            time you need here, then work quickly.
          </p>
          <button
            className="primary"
            onClick={() => void begin()}
            disabled={busy}
            style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}
          >
            Open the toolchain
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* The target. Fixed height: nothing above the module list may move as the build changes. */}
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.55rem 0.7rem',
        marginBottom: '0.6rem', background: '#0d1117',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.45rem' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>budget</span>
          <div style={{ flex: '1 1 100px', minWidth: 80, height: 8, background: '#111', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              width: `${Math.min(100, budget > 0 ? (bytes / budget) * 100 : 0)}%`, height: '100%',
              background: spare < 0 ? '#f87171' : spare <= budget * 0.1 ? '#fbbf24' : '#4ade80',
              transition: 'width 0.15s',
            }} />
          </div>
          <strong style={{ ...row, minWidth: '7.5rem', color: spare < 0 ? '#f87171' : '#cbd5e1' }}>
            {bytes} / {budget} B
          </strong>
          <span style={{ ...row, fontSize: '0.74rem', minWidth: '5.5rem', color: 'var(--text-muted)' }}>
            {spare >= 0 ? `${spare} B spare` : `${-spare} B over`}
          </span>
        </div>

        {axes.map((name, i) => (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.2rem' }}>
            <span style={{ ...row, fontSize: '0.72rem', minWidth: '4.6rem', color: 'var(--text-muted)' }}>{name}</span>
            <div style={{ flex: '1 1 100px', minWidth: 80, height: 6, background: '#111', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{
                width: `${Math.min(100, (required[i] ?? 0) > 0 ? (totals[i] / required[i]) * 100 : 0)}%`,
                height: '100%', background: axisShort[i] === 0 ? '#4ade80' : 'var(--primary-color)',
                transition: 'width 0.15s',
              }} />
            </div>
            <span style={{ ...row, minWidth: '7.5rem', color: axisShort[i] === 0 ? '#4ade80' : '#cbd5e1' }}>
              {totals[i]} / {required[i] ?? 0}
            </span>
            <span style={{ ...row, fontSize: '0.74rem', minWidth: '5.5rem', color: axisShort[i] > 0 ? '#fbbf24' : '#4b5563' }}>
              {axisShort[i] > 0 ? `short ${axisShort[i]}` : 'met'}
            </span>
          </div>
        ))}

        <div style={{ ...row, fontSize: '0.72rem', color: '#6b7280', marginTop: '0.4rem' }}>
          signature detection: at most <strong style={{ color: '#cbd5e1' }}>{cap}</strong> module
          {cap === 1 ? '' : 's'} of{' '}
          {known.length > 0
            ? <strong style={{ color: '#f87171' }}>{known.join(' and ')}</strong>
            : `${live?.detectedFamilies ?? 1} undisclosed famil${(live?.detectedFamilies ?? 1) === 1 ? 'y' : 'ies'}`}
          {known.length === 0 && ' — staying at or under it in every family is always safe'}
        </div>
      </div>

      {/* The library */}
      <div style={{ display: 'grid', gap: '0.25rem', marginBottom: '0.6rem' }}>
        {modules.map(m => {
          const on = chosen.has(m.id);
          const needsName = m.needs === null ? null : modules.find(x => x.id === m.needs)?.name ?? '?';
          const needsMissing = on && m.needs !== null && !chosen.has(m.needs);
          const familyHot = on && cap > 0 && (familyCounts.get(m.family) ?? 0) > cap;
          return (
            <button
              key={m.id}
              onClick={() => toggle(m.id)}
              disabled={busy}
              style={{
                ...row, textAlign: 'left', padding: '0.3rem 0.55rem', borderRadius: 4, cursor: 'pointer',
                border: `1px solid ${needsMissing || familyHot ? '#b45309' : on ? '#166534' : '#232833'}`,
                background: on ? 'rgba(22,101,52,0.18)' : '#0d1117',
                color: on ? '#e2e8f0' : '#9aa4b2',
                display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap',
              }}
            >
              <span style={{ minWidth: '1rem', color: on ? '#4ade80' : '#4b5563' }}>{on ? '✓' : '·'}</span>
              <strong style={{ minWidth: '9rem' }}>{m.name}</strong>
              <span style={{ minWidth: '4.6rem', fontSize: '0.72rem', color: known.includes(m.family) ? '#f87171' : '#6b7280' }}>
                {m.family}
              </span>
              <span style={{ minWidth: '4rem', fontSize: '0.74rem' }}>{m.bytes} B</span>
              <span style={{ minWidth: '11rem', fontSize: '0.74rem', color: '#60a5fa' }}>
                {axes.map((a, i) => (m.gain[i] > 0 ? `${a.slice(0, 3)}+${m.gain[i]}` : null))
                     .filter(Boolean).join(' ')}
              </span>
              <span style={{ fontSize: '0.72rem', color: needsMissing ? '#fbbf24' : '#4b5563' }}>
                {needsName ? `needs ${needsName}` : ''}
              </span>
            </button>
          );
        })}
      </div>

      {/* Reserved slot: a report or a note must not move the buttons below it. */}
      <div style={{ minHeight: '2.6rem', marginBottom: '0.5rem' }}>
        {note && <div style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>{note}</div>}
        {!note && missingDeps.length > 0 && (
          <div style={{ ...row, fontSize: '0.76rem', color: '#fbbf24' }}>
            {missingDeps.map(d => `${d.module} needs ${d.needs}`).join(' · ')}
          </div>
        )}
        {!note && missingDeps.length === 0 && overCap.length > 0 && (
          <div style={{ ...row, fontSize: '0.76rem', color: known.length > 0 ? '#f87171' : '#fbbf24' }}>
            {overCap.map(([f, n]) => `${n} of ${f}, the limit is ${cap}`).join(' · ')}
            {known.length > 0 ? ' — that family IS watched' : ' — safe only if that family is not the watched one'}
          </div>
        )}
        {!note && (live?.report?.length ?? 0) > 0 && missingDeps.length === 0 && overCap.length === 0 && (
          <div style={{ ...row, fontSize: '0.76rem', color: '#f87171' }}>
            last trial: {live?.report?.join(' · ')}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => onSubmit({ modules: picked })}
          disabled={busy || picked.length === 0}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
        >
          Compile{readyLooking ? '' : ' anyway'}
        </button>
        {(live?.dryRunsGranted ?? 0) > 0 && (
          <button
            onClick={() => void dryRun()}
            disabled={busy || (live?.dryRunsLeft ?? 0) === 0 || picked.length === 0}
            style={{ ...row, fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
            title="Reports which constraint fails — never what to add"
          >
            Trial compile ({live?.dryRunsLeft ?? 0})
          </button>
        )}
        <span style={{ ...row, fontSize: '0.72rem', color: '#4b5563' }}>
          {picked.length} module{picked.length === 1 ? '' : 's'} selected
        </span>
      </div>
    </div>
  );
}
